"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useAuth } from "@/context/AuthContext";

/** Routes that must never be blocked (public + the legal pages themselves). */
const PUBLIC_PREFIXES = ["/auth", "/privacy", "/subprocessors"];

function isPublicPath(pathname: string): boolean {
    if (pathname === "/") return true;
    return PUBLIC_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/**
 * First-login clickwrap gate (LG-3). Once a user is authenticated, blocks the
 * app until they accept the Privacy Policy + Pilot Evaluation Terms. Acceptance
 * is recorded server-side (legal_acceptances + audit log). Public/legal pages
 * are never blocked so a partner can read the terms before agreeing.
 */
export function PilotConsentGate() {
    const { isAuthenticated, isLoading } = useAuth();
    const pathname = usePathname();

    const [status, setStatus] = useState<"unknown" | "accepted" | "needed">("unknown");
    const [checked, setChecked] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const onPublic = isPublicPath(pathname ?? "/");

    useEffect(() => {
        let cancelled = false;
        if (isLoading || !isAuthenticated || onPublic) return;
        if (status !== "unknown") return;

        (async () => {
            try {
                const res = await fetch("/api/legal/consent", { credentials: "include" });
                const data = await res.json().catch(() => null);
                if (cancelled) return;
                if (res.ok && data?.success) {
                    setStatus(data.accepted ? "accepted" : "needed");
                } else {
                    // If we can't determine status, fail open (don't lock the user
                    // out of the app over a transient error) but try again later.
                    setStatus("accepted");
                }
            } catch {
                if (!cancelled) setStatus("accepted");
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, isLoading, onPublic, status]);

    const handleAccept = async () => {
        if (!checked || submitting) return;
        setSubmitting(true);
        setError(null);
        try {
            const res = await fetch("/api/legal/consent", {
                method: "POST",
                credentials: "include",
            });
            const data = await res.json().catch(() => null);
            if (res.ok && data?.success) {
                setStatus("accepted");
            } else {
                setError("Could not record your acceptance. Please try again.");
            }
        } catch {
            setError("Could not record your acceptance. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (isLoading || !isAuthenticated || onPublic || status !== "needed") {
        return null;
    }

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/55 p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pilot-consent-title"
        >
            <div className="w-full max-w-lg rounded-xl border border-line bg-card p-5 sm:p-6 text-ink shadow-pop">
                <h2 id="pilot-consent-title" className="text-[21px] leading-tight font-extrabold tracking-[-0.02em]">
                    Before you continue
                </h2>
                <p className="mt-3 text-sm text-ink-2">
                    To use this Savira evaluation, please review and accept the terms below. You only need to do this
                    once.
                </p>

                <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-lg bg-sunk p-4">
                    <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => setChecked(e.target.checked)}
                        className="mt-1 h-4 w-4 shrink-0 accent-accent"
                    />
                    <span className="text-sm text-ink-2">
                        I have read and agree to the{" "}
                        <Link
                            href="/privacy"
                            target="_blank"
                            className="font-bold text-accent underline underline-offset-4 hover:text-accent-hover"
                        >
                            Privacy Policy
                        </Link>{" "}
                        and the Pilot Evaluation Terms, and I confirm I am authorised to do so on behalf of my
                        organisation.
                    </span>
                </label>

                {error && <p className="mt-3 text-sm text-bad">{error}</p>}

                <button
                    type="button"
                    onClick={handleAccept}
                    disabled={!checked || submitting}
                    className="mt-5 w-full rounded-lg border border-accent bg-accent px-5 py-3 text-[15px] font-bold text-accent-ink transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-40"
                >
                    {submitting ? "Recording…" : "I Agree"}
                </button>

                <p className="mt-4 text-center text-xs font-semibold text-ink-3">
                    Need the full document set? Contact privacy@savira.io
                </p>
            </div>
        </div>
    );
}
